import type { ResultListStateQuery } from '../state/state.types';
import type {
  FilterItem,
  LatLonPair,
  SearchField,
} from '../models/search-result';
import {
  ensureSearchFields,
  initialSearchState,
  normalizeFilterSectionId,
} from './initialState';

const MANAGED_URL_PARAMS = new Set([
  'filters',
  'bbox',
  'settings',
  'search',
  'q',
  'pageIndex',
  'pageSize',
  'start',
  'rows',
  'sortBy',
  'place',
  'place.id',
  'place.finder',
  'place.op',
  'view',
  'sort',
  'disp',
  'basemap',
  'searchScope',
  'scopeFq',
  'scopeLabel',
]);

export const buildUrlFromQuery = (
  query: ResultListStateQuery,
  path?: string,
): string => {
  const existingParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const params = new URLSearchParams();

  existingParams.forEach((value, key) => {
    if (!MANAGED_URL_PARAMS.has(key) && !key.startsWith('f.')) {
      params.set(key, value);
    }
  });

  if (query?.filters && query.filters.length > 0) {
    params.set(
      'filters',
      query.filters
        .filter((filter) => filter?.sectionId && filter?.id)
        .map((filter) => `${filter.sectionId}:|:${filter.id}`)
        .join(';;'),
    );
  }

  if (query?.spatialFilter?.bbox && query.spatialFilter.bbox.length > 0) {
    params.set(
      'bbox',
      query.spatialFilter.bbox
        .map((pair: LatLonPair) => `${pair.longitude},${pair.latitude}`)
        .join(';;'),
    );
  }

  const settingsParam = Array.from(
    new Set([...ensureSearchFields(query?.settings?.searchFields)]),
  ).join(',');
  params.set('settings', settingsParam);

  if (query?.search) {
    params.set('search', query.search);
  }

  if (query?.pageIndex && query.pageIndex > 0) {
    params.set('pageIndex', query.pageIndex.toString());
  }

  if (query?.pageSize && query.pageSize !== 25) {
    params.set('pageSize', query.pageSize.toString());
  }

  if (query?.sortBy && query.sortBy !== 'date') {
    params.set('sortBy', query.sortBy);
  }

  if (query?.place?.name) {
    params.set('place', query.place.name);
    if (query.place.id) {
      params.set('place.id', query.place.id);
    }
    if (query.place.finder) {
      params.set('place.finder', query.place.finder);
    }
  }

  if (query?.spatialOperator && query.spatialOperator !== 'within') {
    params.set('place.op', query.spatialOperator);
  } else if (query?.place?.name) {
    params.set('place.op', 'within');
  }

  if (query?.viewType) {
    params.set('view', query.viewType);
  }
  if (query?.voyagerSort) {
    params.set('sort', query.voyagerSort);
  }
  if (query?.displayId) {
    params.set('disp', query.displayId);
  }
  if (query?.basemap) {
    params.set('basemap', query.basemap);
  }
  if (query?.searchScope && query.searchScope !== 'all') {
    params.set('searchScope', query.searchScope);
  }
  if (query?.scopeFq) {
    params.set('scopeFq', query.scopeFq);
  }
  if (query?.scopeLabel) {
    params.set('scopeLabel', query.scopeLabel);
  }
  if (query?.facetParams) {
    Object.entries(query.facetParams).forEach(([key, value]) => {
      params.set(key, value);
    });
  }

  const queryString = params.toString();
  if (typeof window === 'undefined') {
    const basePath = path || '';
    return queryString ? `${basePath}?${queryString}` : basePath;
  }

  const basePath = path || window.location.pathname || '';
  return queryString ? `${basePath}?${queryString}` : basePath;
};

export const parseQueryFromUrl = (
  url: URLSearchParams,
): {
  query: ResultListStateQuery;
  activeFilter: string;
} => {
  const baseQuery = initialSearchState.query!;

  let pageIndex = parseInt(url.get('pageIndex') || '0', 10) || 0;
  let pageSize = parseInt(url.get('pageSize') || '25', 10) || 25;

  const startParam = url.get('start');
  const rowsParam = url.get('rows');
  if (rowsParam) {
    pageSize = parseInt(rowsParam, 10) || 25;
  }
  if (startParam) {
    const start = parseInt(startParam, 10) || 0;
    pageIndex = Math.floor(start / pageSize);
  }

  const sortBy = url.get('sortBy') || 'date';
  const search = url.get('search') || url.get('q') || '';
  const filtersParam = url.get('filters') || '';
  const bboxParam = url.get('bbox') || '';
  const settingsParam = url.get('settings') || '';

  const viewParam = url.get('view') || '';
  const sortParam = url.get('sort') || '';
  const dispParam = url.get('disp') || '';
  const basemapParam = url.get('basemap') || '';
  const searchScopeParam = url.get('searchScope') || '';
  const scopeFqParam = url.get('scopeFq') || '';
  const scopeLabelParam = url.get('scopeLabel') || '';

  const facetParams: Record<string, string> = {};
  url.forEach((value, key) => {
    if (key.startsWith('f.') && key.includes('.facet.')) {
      facetParams[key] = value;
    }
  });

  const placeName = url.get('place') || '';
  const placeId = url.get('place.id') || '';
  const placeFinder = url.get('place.finder') || '';
  const placeOp = url.get('place.op') || 'within';

  let filters: FilterItem[] = [];
  if (filtersParam) {
    filters = filtersParam.split(';;').reduce((acc, filterPair) => {
      if (!filterPair) return acc;
      const normalizedPair = filterPair.replace(/%7C/gi, ':|:');
      const [sectionIdRaw, idRaw] = normalizedPair.split(':|:');
      if (!sectionIdRaw || !idRaw) return acc;
      const sectionId = normalizeFilterSectionId(
        decodeURIComponent(sectionIdRaw.trim()),
      );
      const id = decodeURIComponent(idRaw.trim());
      if (!sectionId) return acc;
      acc.push({
        sectionId,
        id,
        label: id,
        isChecked: true,
      });
      return acc;
    }, [] as FilterItem[]);
  }

  const fqParams = url.getAll('fq');
  if (fqParams.length > 0) {
    fqParams.forEach((fq) => {
      if (!fq) return;
      const colonIndex = fq.indexOf(':');
      if (colonIndex > 0) {
        const sectionId = normalizeFilterSectionId(fq.substring(0, colonIndex));
        let id = fq.substring(colonIndex + 1);
        if (!sectionId) {
          return;
        }
        if (
          (id.startsWith('"') && id.endsWith('"')) ||
          (id.startsWith("'") && id.endsWith("'"))
        ) {
          id = id.slice(1, -1);
        }
        const exists = filters.some(
          (filter) => filter.sectionId === sectionId && filter.id === id,
        );
        if (!exists) {
          filters.push({
            sectionId,
            id,
            label: id,
            isChecked: true,
          });
        }
      }
    });
  }

  let bbox: LatLonPair[] = [];
  if (bboxParam) {
    bbox = bboxParam.split(';;').reduce(
      (acc, pair) => {
        if (!pair) return acc;
        const [lng, lat] = pair.split(',').map(Number);
        if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
          acc.push({ longitude: lng, latitude: lat });
        }
        return acc;
      },
      [] as { longitude: number; latitude: number }[],
    );
  }

  let placeCoordinates: {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
  } | null = null;
  if (placeName && !bboxParam) {
    const coordParts = placeName.replace(/\+/g, ' ').trim().split(/\s+/);
    if (coordParts.length === 4) {
      const [minLng, minLat, maxLng, maxLat] = coordParts.map(Number);
      if ([minLng, minLat, maxLng, maxLat].every((n) => !Number.isNaN(n))) {
        placeCoordinates = { minLng, minLat, maxLng, maxLat };
        bbox = [
          { longitude: minLng, latitude: minLat },
          { longitude: maxLng, latitude: minLat },
          { longitude: maxLng, latitude: maxLat },
          { longitude: minLng, latitude: maxLat },
        ];
      }
    }
  }

  const searchFields = ensureSearchFields(
    settingsParam
      ? settingsParam
          .split(',')
          .filter((field): field is SearchField =>
            ['any', 'title', 'abstract', 'keyword'].includes(field),
          )
      : baseQuery.settings?.searchFields,
  );

  const place =
    placeName && !placeCoordinates
      ? {
          name: placeName,
          id: placeId,
          finder: placeFinder || 'esri',
        }
      : undefined;

  return {
    query: {
      ...baseQuery,
      filters,
      spatialFilter: {
        bbox,
        isChecked: bbox.length > 0,
      },
      pageIndex,
      pageSize,
      sortBy,
      search,
      place,
      spatialOperator: placeOp as 'within' | 'intersects' | 'contains',
      locationQuery: placeName,
      searchScope: searchScopeParam || 'all',
      scopeFq: scopeFqParam || undefined,
      scopeLabel: scopeLabelParam || undefined,
      settings: {
        searchFields,
      },
      viewType: viewParam
        ? (viewParam as 'card' | 'table' | 'grid' | 'summary' | 'map')
        : undefined,
      voyagerSort: sortParam || undefined,
      displayId: dispParam || undefined,
      basemap: basemapParam || undefined,
      facetParams:
        Object.keys(facetParams).length > 0 ? facetParams : undefined,
    },
    activeFilter: filters.length > 0 ? 'filtered' : 'all',
  };
};
